import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import MockExamQuestionForm, { QUESTION_TYPES } from '../admincomponents/MockExamQuestionForm';
import MockExamBulkUpload from '../admincomponents/MockExamBulkUpload';

const apiUrl = process.env.REACT_APP_API_URL;

const actionIconStyle = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const solidRed = { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 };

const emptyQuestion = {
  question_type: 'single_choice',
  question_text: '',
  marks: 1,
  negative_marks: 0,
  options: [{ text: '', is_correct: false }, { text: '', is_correct: false }],
  accepted_answers: [''],
  case_sensitive: false,
  numerical_correct_value: '',
  numerical_tolerance: 0,
  difficulty_level: '',
  explanation: '',
};

function typeLabel(type) {
  return (QUESTION_TYPES.find((t) => t.value === type) || {}).label || type;
}

function QuestionPreview({ formData }) {
  return (
    <div className="p-16" style={{ background: '#f6faf8', borderRadius: 12, border: '1px solid #e8f5ee' }}>
      <div className="text-13 text-gray-400 mb-8">{typeLabel(formData.question_type)} · {formData.marks || 0} mark(s)</div>
      <div className="fw-semibold mb-12">{formData.question_text || <span className="text-gray-400">(question text)</span>}</div>
      {['single_choice', 'multiple_choice', 'true_false'].includes(formData.question_type) && (
        <div className="d-flex flex-column gap-8">
          {(formData.options || []).map((o, idx) => (
            <div key={idx} className="d-flex align-items-center gap-8">
              <input type={formData.question_type === 'multiple_choice' ? 'checkbox' : 'radio'} checked={false} disabled readOnly />
              <span className={o.is_correct ? 'text-success fw-semibold' : ''}>{o.text || `Option ${idx + 1}`}</span>
              {o.is_correct && <span className="badge bg-success-subtle text-success-emphasis">Correct</span>}
            </div>
          ))}
        </div>
      )}
      {['short_text', 'fill_blank'].includes(formData.question_type) && (
        <div>
          <input type="text" className="form-control py-9" placeholder="Student types answer here" disabled style={{ maxWidth: 300 }} />
          <div className="text-13 text-gray-400 mt-8">Accepted: {(formData.accepted_answers || []).filter(Boolean).join(', ') || '—'}</div>
        </div>
      )}
      {formData.question_type === 'numerical' && (
        <div>
          <input type="text" className="form-control py-9" placeholder="Student types number here" disabled style={{ maxWidth: 200 }} />
          <div className="text-13 text-gray-400 mt-8">
            Correct: {formData.numerical_correct_value || '—'} ± {formData.numerical_tolerance || 0}
          </div>
        </div>
      )}
      {formData.question_type === 'long_answer' && (
        <textarea className="form-control py-9" rows={3} placeholder="Student types a longer answer here" disabled />
      )}
      {formData.explanation && (
        <div className="text-13 text-gray-500 mt-12"><strong>Explanation:</strong> {formData.explanation}</div>
      )}
    </div>
  );
}

function AdminMockExamQuestions() {
  const { bankId } = useParams();
  const [bankName, setBankName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyQuestion);
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchBankInfo = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/banks`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        const bank = (response.data.banks || []).find((b) => String(b.id) === String(bankId));
        if (bank) setBankName(bank.name);
      }
    } catch (err) {
      // non-fatal
    }
  }, [bankId]);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const params = {};
      if (typeFilter) params.questionType = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/banks/${bankId}/questions`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) {
        setQuestions(response.data.questions || []);
      } else {
        setError(response.data.message || 'Failed to fetch questions.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching questions.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankId, typeFilter, statusFilter]);

  useEffect(() => { fetchBankInfo(); }, [fetchBankInfo]);
  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const resetForm = () => {
    setFormData(emptyQuestion);
    setEditingId(null);
    setShowForm(false);
    setShowPreview(false);
    setError('');
  };

  const startEdit = (q) => {
    setFormData({
      question_type: q.question_type,
      question_text: q.question_text,
      marks: q.marks,
      negative_marks: q.negative_marks,
      options: q.options_json ? JSON.parse(q.options_json) : [{ text: '', is_correct: false }, { text: '', is_correct: false }],
      accepted_answers: q.accepted_answers_json ? JSON.parse(q.accepted_answers_json) : [''],
      case_sensitive: !!q.case_sensitive,
      numerical_correct_value: q.numerical_correct_value ?? '',
      numerical_tolerance: q.numerical_tolerance ?? 0,
      difficulty_level: q.difficulty_level || '',
      explanation: q.explanation || '',
    });
    setEditingId(q.id);
    setShowForm(true);
    setShowPreview(false);
  };

  const startDuplicate = (q) => {
    startEdit(q);
    setEditingId(null); // duplicating creates a new question, not an update
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      let response;
      if (editingId) {
        response = await axios.put(`${apiUrl}/api/admin/mock-exams/questions/${editingId}`, formData, { headers });
      } else {
        response = await axios.post(`${apiUrl}/api/admin/mock-exams/banks/${bankId}/questions`, formData, { headers });
      }
      if (response.data.success) {
        setSuccess(editingId ? 'Question updated.' : 'Question created.');
        resetForm();
        fetchQuestions();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save question.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving question.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (q) => {
    try {
      const token = getToken();
      const newStatus = q.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${apiUrl}/api/admin/mock-exams/questions/${q.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      fetchQuestions();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status.');
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm('Delete this question? Past attempts that used it keep their own snapshot and are unaffected.')) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/mock-exams/questions/${q.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) fetchQuestions();
      else setError(response.data.message || 'Failed to delete question.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting question.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-mock-exam-banks" className="text-gray-200 fw-normal text-15 hover-text-main-600">Question Banks</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{bankName || 'Questions'}</span></li>
              </ul>
            </div>
            <div className="d-flex gap-8 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-main rounded-pill py-9"
                onClick={() => { setShowBulk((v) => !v); setShowForm(false); }}
              >
                <i className={`ph ${showBulk ? 'ph-x' : 'ph-upload-simple'} me-2`} /> {showBulk ? 'Close Bulk Upload' : 'Bulk Upload'}
              </button>
              <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); setShowBulk(false); } }}>
                <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add Question'}
              </button>
            </div>
          </div>

          {showBulk && (
            <MockExamBulkUpload
              bankId={bankId}
              onClose={() => setShowBulk(false)}
              onImported={() => { fetchQuestions(); setSuccess('Questions imported.'); }}
            />
          )}

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{editingId ? 'Edit Question' : 'Add New Question'}</h5>
                <button type="button" className="btn btn-sm btn-outline-main py-4 px-12" onClick={() => setShowPreview(!showPreview)}>
                  {showPreview ? 'Back to Edit' : 'Preview'}
                </button>
              </div>
              <div className="card-body">
                {showPreview ? (
                  <QuestionPreview formData={formData} />
                ) : (
                  <form onSubmit={handleSubmit}>
                    <MockExamQuestionForm formData={formData} setFormData={setFormData} disabled={saving} />
                    <div className="d-flex justify-content-end gap-8 mt-20">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>Cancel</button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Question'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Question Type</label>
                  <select className="form-control py-9" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">All Types</option>
                    {QUESTION_TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Status</label>
                  <select className="form-control py-9" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Questions ({questions.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : questions.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No questions yet. Add one above.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Type</th>
                        <th>Marks</th>
                        <th>Difficulty</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((q) => (
                        <tr key={q.id}>
                          <td style={{ maxWidth: 340 }}>{q.question_text.length > 90 ? `${q.question_text.slice(0, 90)}...` : q.question_text}</td>
                          <td>{typeLabel(q.question_type)}</td>
                          <td>{q.marks}{Number(q.negative_marks) > 0 ? ` (-${q.negative_marks})` : ''}</td>
                          <td className="text-capitalize">{q.difficulty_level || '—'}</td>
                          <td>
                            <span className={`badge ${q.status === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-neutral-200 text-neutral-600'}`}>
                              {q.status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-6">
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Edit" onClick={() => startEdit(q)}>
                                <i className="ph ph-pencil-simple" />
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Duplicate" onClick={() => startDuplicate(q)}>
                                <i className="ph ph-copy" />
                              </button>
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title={q.status === 'active' ? 'Deactivate' : 'Activate'} onClick={() => toggleStatus(q)}>
                                <i className={`ph ${q.status === 'active' ? 'ph-eye-slash' : 'ph-eye'}`} />
                              </button>
                              <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidRed }} title="Delete" onClick={() => handleDelete(q)}>
                                <i className="ph ph-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamQuestions;
